import { getUniqueVariantId } from 'shared/studio/ProductDetailPage/utils';
import type { Variant } from 'state/store/types';

type Args = {
  urlQuery: Record<string, any>;
  variants: Variant[] | null;
  store: string;
  pdpType?: 'productView' | 'otpView' | 'bundleView' | string | null;
};

export function resolveInitialPdpState({ urlQuery, variants, store, pdpType }: Args) {
  if (!variants?.length) {
    return { flavor: null, quantity: 1, selectedVariantId: '', isOtpProduct: false };
  }

  const planParam = typeof urlQuery.plan === 'string' ? urlQuery.plan : null;

  // Lane availability for current store
  const laneHasSub = variants.some(
    (v) => v.subscriptionVariantIds?.some((i) => !i.targetStore || i.targetStore === store)
  );
  const laneHasOtp = variants.some(
    (v) => v.oneTimeVariantId?.some((i) => !i.targetStore || i.targetStore === store)
  );

  // Decide lane
  const isOtpByContext =
    planParam === 'one_time'
      ? true
      : planParam === 'subscription'
      ? false
      : pdpType === 'otpView'
      ? true
      : pdpType === 'productView'
      ? false
      : !laneHasSub && laneHasOtp
      ? true
      : laneHasSub && !laneHasOtp
      ? false
      : false; // default to SUB if both available and nothing explicit

  // Desired quantity
  const desiredQty = Number(urlQuery.qty ?? urlQuery.quantity);
  const byQty = Number.isFinite(desiredQty)
    ? variants.find((v) => v.quantity === desiredQty) ?? null
    : null;

  const laneFilter = (v: Variant) =>
    isOtpByContext
      ? v.oneTimeVariantId?.some((i) => !i.targetStore || i.targetStore === store)
      : v.subscriptionVariantIds?.some((i) => !i.targetStore || i.targetStore === store);

  // Choose a base variant that exists in the chosen lane
  const baseVariant =
    byQty && laneFilter(byQty) ? byQty : variants.find(laneFilter) ?? variants[0] ?? null;

  // Flavor (URL → lane → null)
  const urlFlavor =
    typeof urlQuery.flavor === 'string' && urlQuery.flavor.trim() ? urlQuery.flavor : null;

  const laneFlavor = isOtpByContext
    ? baseVariant?.oneTimeVariantId?.find((i) => !i.targetStore || i.targetStore === store)
        ?.flavorQueryParam ?? null
    : baseVariant?.subscriptionVariantIds?.find((i) => !i.targetStore || i.targetStore === store)
        ?.flavorQueryParam ?? null;

  const flavor = urlFlavor ?? laneFlavor ?? null;

  const selectedVariantId = baseVariant
    ? getUniqueVariantId(baseVariant, flavor ?? '', { isOtp: isOtpByContext, store })
    : '';

  const quantity = baseVariant?.quantity ?? (isOtpByContext ? 1 : variants[0]?.quantity ?? 1);

  return { flavor, quantity, selectedVariantId, isOtpProduct: isOtpByContext };
}
